import { Injectable } from '@angular/core';
import moment from 'moment-timezone';
import {
  BankAccountType,
  BankDetails,
  GenerateBankDetailsPdfRequestContent,
  GetGenericFeesData,
  PostContactFormRequestContent,
  PostEmailUnsubscribeRequestContent,
} from 'src/app/api/autogenerated';
import { GetCostsFromFircResponseModel } from 'src/app/api/autogenerated/types.gen';
import { FiraBreakdownModel } from 'src/app/types/FiraBreakdown';
import { Profile } from 'src/app/types/Profile';
import { PayoutFeesResponse } from 'src/app/types/responses/PayoutFeesResponse';
import { UnsubscribeOptions } from 'src/app/types/UnsubscribeOptions';
import { Business } from '../../../types/Business';
import { PaymentMethodDetails } from '../../../types/PaymentDetails';
import { SharedProfileData } from '../../data/shared-profile-data';
import { IPublicService } from '../IPublicService';
import { mockPayoutFeesList, mockProfile } from '../mock/mock-data';
import { OpenApiService } from '../open-api.service';

export function convertFircApiToFiraBreakdown(
  apiResponse: GetCostsFromFircResponseModel,
): FiraBreakdownModel {
  const fircResponseModel = apiResponse.firc_response_model ?? {};

  const mmr = fircResponseModel.calculations?.mmr_rate ?? 0;
  const userFx = fircResponseModel.conversion_rate ?? 0;
  const fxSpread = mmr - userFx;
  const usdAmount = fircResponseModel.source_amount?.value ?? 0;
  const fxSpreadValue =
    fircResponseModel.calculations?.service_charge?.value ?? 0;
  const effectiveTotalCostCalc = fxSpread * usdAmount;

  return {
    id: apiResponse.id || '',
    bankName: fircResponseModel.bank_name || '',
    chargedAmount: fircResponseModel.calculations?.gross ?? 0,
    chargedPaise: fircResponseModel.calculations?.fx_spread?.paise ?? 0,
    chargeBps: fircResponseModel.calculations?.fx_spread?.bps ?? 0,
    chargedAmountCurrency:
      fircResponseModel.converted_amount?.currency || 'INR',
    midMarketRate: mmr,
    transactionDate: fircResponseModel.date || '',
    purposeCode: fircResponseModel.purpose_code || '',
    usdAmount: usdAmount,
    userFxRate: userFx,
    inrAfterFx: fircResponseModel.converted_amount?.value ?? 0,
    fxSpread: fxSpreadValue,
    effectiveTotalCost: fircResponseModel.calculations?.gross ?? 0,
    effectiveFxSpreadInInr: fxSpread,
    effectiveTotalCostCalc: effectiveTotalCostCalc,
  };
}

@Injectable({
  providedIn: 'root',
})
export class PublicService implements IPublicService {
  private profile: Profile = { ...mockProfile };

  private payoutFeesList: PayoutFeesResponse[] = mockPayoutFeesList;

  constructor(
    private sharedProfileData: SharedProfileData,
    private openApiService: OpenApiService,
  ) {}

  async getGenericFees(
    sellCurrency: string,
    buyCurrency: string,
  ): Promise<{
    conversionRate: number;
    validUpto: string;
    midMarketRate: number;
  }> {
    // let apiSellCurrency: Currency
    // let apiBuyCurrency: Currency

    // if(sellCurrency == "USD") {
    //     apiSellCurrency = "USD";
    // }

    // if(buyCurrency == "INR") {
    //     apiBuyCurrency = "INR";
    // }

    let params: GetGenericFeesData['query'] = {
      sell_currency: 'USD',
      buy_currency: 'INR',
    };

    let responseFromApi = await this.openApiService.getGenericFees(params);
    const validUpto = moment()
      .tz('Asia/Kolkata')
      .format(`MMM D, YYYY, HH:mm [IST]`);

    const midMarketRate = responseFromApi?.data?.mid_market_rate ?? 0;
    const userConversionRate = midMarketRate - midMarketRate * 0.01;

    return {
      conversionRate: userConversionRate,
      validUpto: validUpto,
      midMarketRate: midMarketRate,
    };
  }

  async getUserUnsubscribeStatus(hash: string): Promise<boolean> {
    let responseFromApi =
      await this.openApiService.getEmailUnsubscribeStatus(hash);

    return responseFromApi.data.unsubscribed;
  }

  async unsubscribeEmailNotification(
    hash: string,
    reason: UnsubscribeOptions,
    feedback: string,
  ): Promise<boolean> {
    let body: PostEmailUnsubscribeRequestContent = {
      reason: reason,
      other_reason: feedback,
    };

    let responseFromApi = await this.openApiService.postEmailUnsubscribe(
      hash,
      body,
    );
    return responseFromApi.data.unsubscribed;
  }

  private toBankAccountType(type: string): BankAccountType {
    switch (type) {
      case 'ACH':
        return 'ACH' as BankAccountType;
      case 'WIRE':
        return 'WIRE' as BankAccountType;
      case 'FEDWIRE':
        return 'FEDWIRE' as BankAccountType;
      case 'RTP':
        return 'RTP' as BankAccountType;
      case 'SWIFT':
        return 'SWIFT' as BankAccountType;
      case 'EFT':
        return 'EFT' as BankAccountType;
      case 'FPS':
        return 'FPS' as BankAccountType;
      case 'SEPA Credit':
        return 'SEPA_CREDIT' as BankAccountType;
      case 'SEPA Instant':
        return 'SEPA_INSTANT' as BankAccountType;
      default:
        return 'ACH' as BankAccountType;
    }
  }

  private toBankDetails(
    paymentMethodDetails: PaymentMethodDetails[],
    currency: string,
  ): BankDetails[] {
    const normalizedCurrency = currency.toUpperCase();

    return paymentMethodDetails.map((detail) => {
      const bankDetail: BankDetails = {
        currency: normalizedCurrency,
        bank_name: detail.bankName,
        bank_address: detail.bankAddress,
        account_type: detail.accountType,
        number: detail.accountNumber,
        type: this.toBankAccountType(detail.paymentMethodType),
      };

      const routing = detail.achRouting;
      switch (detail.paymentMethodType) {
        case 'SWIFT':
          bankDetail.global_wire = routing;
          break;
        case 'FEDWIRE':
        case 'WIRE':
          bankDetail.domestic_wire = routing;
          break;
        case 'FPS':
        case 'SEPA Instant':
        case 'RTP':
          bankDetail.domestic_fast_credit = routing;
          break;
        case 'ACH':
        case 'EFT':
        case 'SEPA Credit':
        default:
          bankDetail.domestic_credit = routing;
          break;
      }

      return bankDetail;
    });
  }

  async generateBankDetailsPDF(
    beneficiaryName: string,
    clientDetails: Partial<Business>,
    selectedBankDetails: PaymentMethodDetails[],
    currency = 'USD',
  ): Promise<Blob> {
    let requestBody: GenerateBankDetailsPdfRequestContent = {
      beneficiary_name: beneficiaryName,
      payer_name: clientDetails.name || '',
      bank_details: this.toBankDetails(selectedBankDetails, currency),
    };

    let resp = await this.openApiService.generateBankDetailsPdf(requestBody);
    console.log(resp);
    if (!resp.success) {
      throw new Error(resp.error);
    }
    return new Blob([resp.data], { type: 'application/pdf' });
  }

  async saveContactUsFormSubmission(
    name: string,
    email: string,
    phone: string,
    message: string,
  ): Promise<boolean> {
    if (!name || !email || !phone || !message) {
      throw new Error('All fields are required');
    }

    const data: PostContactFormRequestContent = {
      name,
      email,
      phone,
      message,
    };

    let responseFromApi = await this.openApiService.postContactForm(data);
    return responseFromApi.success;
  }

  async getFIRABreakDown(firaFile: File): Promise<{
    success: boolean;
    data: FiraBreakdownModel | null;
    error: string;
  }> {
    let responseFromApi = await this.openApiService.getFIRABreakDown(firaFile);
    if (responseFromApi.success && responseFromApi.data) {
      return {
        success: true,
        data: convertFircApiToFiraBreakdown(responseFromApi.data),
        error: '',
      };
    }
    return {
      success: false,
      data: null,
      error: responseFromApi.error,
    };
  }

  async getFIRABreakDownById(requestId: String): Promise<{
    success: boolean;
    data: FiraBreakdownModel | null;
    error: string;
  }> {
    let responseFromApi = await this.openApiService.getFIRABreakDownById(
      requestId.toString(),
    );
    if (responseFromApi.success && responseFromApi.data) {
      // Transform GetFircUploadResponseByIdModel to GetCostsFromFircResponseModel format
      const transformedApiResponse: GetCostsFromFircResponseModel = {
        id: requestId.toString(),
        firc_response_model: responseFromApi.data.firc_response_model,
      };

      return {
        success: true,
        data: convertFircApiToFiraBreakdown(transformedApiResponse),
        error: '',
      };
    }
    return {
      success: false,
      data: null,
      error: responseFromApi.error,
    };
  }
}
